/**
 * Platform-agnostic OAuth 2.0 Authorization Code + PKCE (S256) helpers for the
 * Entra interactive sign-in flow (WI #31089, Ryn's Roost mobile).
 *
 * No Angular, no NativeScript, no web crypto imports: the two platform primitives
 * (secure random bytes, SHA-256) are passed IN. That keeps this module pure and
 * unit-testable, and lets the identical logic run under the NativeScript runtime
 * (native SecRandom / CommonCrypto, Android SecureRandom / MessageDigest) and in
 * the browser (crypto.getRandomValues / crypto.subtle). base64url is hand-rolled
 * rather than using btoa/Buffer so it is portable to every runtime.
 *
 * PKCE (RFC 7636) is mandatory for a public-client mobile app: it binds the auth
 * code to this device so an intercepted code cannot be redeemed by an attacker.
 * The interactive browser flow (opening the URL, capturing the redirect) is the
 * caller's concern; this module only shapes and validates the OAuth messages.
 */

/** URL-safe base64 alphabet (RFC 4648 §5): '+' -> '-', '/' -> '_', no padding. */
const B64URL = 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789-_';

/** Encode bytes as base64url without padding. Portable (no btoa, no Buffer). */
export function base64UrlEncode(bytes: Uint8Array): string {
  let out = '';
  for (let i = 0; i < bytes.length; i += 3) {
    const b0 = bytes[i];
    const b1 = i + 1 < bytes.length ? bytes[i + 1] : 0;
    const b2 = i + 2 < bytes.length ? bytes[i + 2] : 0;
    out += B64URL[b0 >> 2];
    out += B64URL[((b0 & 0b11) << 4) | (b1 >> 4)];
    if (i + 1 < bytes.length) out += B64URL[((b1 & 0b1111) << 2) | (b2 >> 6)];
    if (i + 2 < bytes.length) out += B64URL[b2 & 0b111111];
  }
  return out;
}

/** Platform primitive: return `length` cryptographically-secure random bytes. */
export type RandomBytes = (length: number) => Uint8Array;

/** Platform primitive: SHA-256 digest of a UTF-8 string, as raw bytes. */
export type Sha256 = (input: string) => Promise<Uint8Array>;

/**
 * A PKCE code verifier: 32 secure-random bytes -> 43-char base64url string.
 * RFC 7636 §4.1 requires 43-128 chars from the unreserved set; base64url of 32
 * bytes yields exactly 43 unreserved chars with maximum entropy.
 */
export function generateCodeVerifier(randomBytes: RandomBytes): string {
  return base64UrlEncode(randomBytes(32));
}

/** An opaque CSRF `state` value: 16 secure-random bytes -> base64url. */
export function generateState(randomBytes: RandomBytes): string {
  return base64UrlEncode(randomBytes(16));
}

/** The S256 code challenge = base64url(SHA-256(ascii(verifier))) (RFC 7636 §4.2). */
export async function deriveCodeChallenge(verifier: string, sha256: Sha256): Promise<string> {
  return base64UrlEncode(await sha256(verifier));
}

export interface AuthorizeUrlParams {
  /** e.g. https://login.microsoftonline.com/<tenant>/oauth2/v2.0/authorize */
  authorizeEndpoint: string;
  clientId: string;
  redirectUri: string;
  scopes: string[];
  state: string;
  codeChallenge: string;
}

/**
 * Build the Entra /authorize URL for the auth-code + PKCE (S256) flow.
 * `response_mode=query` so the code comes back on the querystring, which
 * `parseAuthorizeRedirect` reads from the captured redirect URL.
 */
export function buildAuthorizeUrl(p: AuthorizeUrlParams): string {
  const params: ReadonlyArray<readonly [string, string]> = [
    ['client_id', p.clientId],
    ['response_type', 'code'],
    ['redirect_uri', p.redirectUri],
    ['response_mode', 'query'],
    ['scope', p.scopes.join(' ')],
    ['state', p.state],
    ['code_challenge', p.codeChallenge],
    ['code_challenge_method', 'S256'],
  ];
  const query = params
    .map(([k, v]) => `${encodeURIComponent(k)}=${encodeURIComponent(v)}`)
    .join('&');
  const sep = p.authorizeEndpoint.includes('?') ? '&' : '?';
  return `${p.authorizeEndpoint}${sep}${query}`;
}

export type RedirectParseResult =
  | { ok: true; code: string }
  | { ok: false; error: string };

/**
 * Parse the redirect URL the system browser returns and validate the CSRF state
 * BEFORE trusting the code. A state mismatch is a hard failure (RFC 6749 §10.12):
 * accepting a code whose state does not match the one we sent would let an
 * attacker inject their own authorization code. An `error` param short-circuits
 * (surfaced with its description). Reads only the query, ignoring any fragment.
 */
export function parseAuthorizeRedirect(
  redirectUrl: string,
  expectedState: string
): RedirectParseResult {
  const params = new Map<string, string>();
  const qIndex = redirectUrl.indexOf('?');
  if (qIndex >= 0) {
    const query = redirectUrl.slice(qIndex + 1).split('#')[0];
    for (const pair of query.split('&')) {
      if (!pair) continue;
      const eq = pair.indexOf('=');
      const rawKey = eq >= 0 ? pair.slice(0, eq) : pair;
      const rawVal = eq >= 0 ? pair.slice(eq + 1) : '';
      params.set(decodeURIComponent(rawKey), decodeURIComponent(rawVal.replace(/\+/g, ' ')));
    }
  }
  const err = params.get('error');
  if (err) return { ok: false, error: params.get('error_description') || err };
  if (params.get('state') !== expectedState) return { ok: false, error: 'state_mismatch' };
  const code = params.get('code');
  if (!code) return { ok: false, error: 'missing_code' };
  return { ok: true, code };
}
